import { Axios } from "axios";
import { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { Action } from "redux";
import { ThunkAction } from "redux-thunk";

import { useThunkDispatch } from "@/redux/dispatcher";
import { IState } from "@/redux/store.types";

export const useReduxApiState = <T, ReturnType, BasicAction extends Action>(
    isLoadingSelector: (state: IState) => boolean,
    getStateSelector: (state: IState) => T,
    loadAction: ThunkAction<ReturnType, IState, Axios, BasicAction>
) => {
    const dispatch = useThunkDispatch();

    const [hasLoaded, setHasLoaded] = useState(false);

    const [{ promise, resolve }] = useState(
        Promise.withResolvers<NonNullable<T> | null>()
    );

    const isLoading = useSelector(isLoadingSelector);
    const state = useSelector(getStateSelector, shallowEqual);

    useEffect(() => {
        if (state || isLoading) {
            setHasLoaded(true);
            return;
        }

        if (hasLoaded) {
            return;
        }

        dispatch(loadAction);
        setHasLoaded(true);
    }, [state, hasLoaded, isLoading, dispatch, loadAction]);

    useEffect(() => {
        if (!hasLoaded) return;
        if (isLoading) return;

        resolve(state ?? null);
    }, [state, hasLoaded, isLoading, resolve]);

    return {
        state: promise,
        loading: isLoading || !hasLoaded
    };
};
